import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { bp } from 'Styles/helpers';
import screen from 'superior-mq';
import objectPath from 'object-path';
import Container from '../../container';
import Button from '../../button';
import Grid from '../../grid';
import LazyImg from '../../lazy-img';
import Subhead from '../../subhead';
import Waves from '../../waves';
import RenderMd from '../../render-md';
import TextWave from '../../text-wave';
import ImageSlideIn from '../../image-slide-in';

const gridStyles = `
  ${screen.below(bp.tablet, `
    grid-template-columns: repeat(10, 1fr);
    grid-template-rows: repeat(4, 1fr);

    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: -130px;
      left: 0;
      z-index: -1;
      background-color: #fff;
      content: '';

      ${screen.below(bp.tablet, `
        bottom: -70px;
      `)}
    }
  `)};
`;

const leftColumnStyles = `
  z-index: 2;
  grid-column: 2 / span 3;
  align-self: end;

  ${screen.below(bp.desktop, `
    grid-column: 1 / span 3;
  `)};

  ${screen.below(bp.tablet, `
    grid-column: 1 / span 6;
    grid-row: 1 / span 3;
  `)};
`;

const centerColumnStyles = `
  position: relative;
  grid-column: 5 / span 4;
  place-self: center;
  z-index: 1;

  &::before {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: -1;
    width: 788px;
    height: 694px;
    background-color: #fff;
    transform: translate(-50%, -50%);
    content: '';

    ${screen.below(bp.tablet, `
      display: none;
    `)};
  }

  ${screen.below(bp.tablet, `
    grid-column: 1 / -1;
    grid-row: 5 / -1;
  `)};
`;

const rightColumnStyles = `
  position: relative;
  z-index: 2;
  grid-column: 10 / -1;

  figure {
    margin-top: 100px;

    ${screen.below(bp.tablet, `
      margin-top: 0;
    `)};
  }

  ${screen.below(bp.tablet, `
    grid-column: 6 / span 6;
    grid-row: 2 / span 3;
  `)};
`;

const AboutSolutions = props => (
  <SolutionsSection id={props.id}>
    <ImageSlideIn>
      <StoryContainer>

        <Grid styles={gridStyles}>

          {props.images && objectPath.has(props.images[0], 'fields.file.url') &&
            <Grid.Item styles={leftColumnStyles}>
              <LeftImage className="js-slide-image">
                <LazyImg
                  width={335}
                  height={224}
                  src={`${props.images[0].fields.file.url}?w=335&h=224&fit=thumb&q=80&fm=jpg&fl=progressive`}
                  alt={props.images[0].fields.description}
                />
              </LeftImage>
            </Grid.Item>
          }

          <Grid.Item styles={centerColumnStyles}>
            <Subhead>{props.subheading}</Subhead>
            <TextWave><h2>{props.title}</h2></TextWave>
            <RenderMd source={props.content} />

            {objectPath.has(props, 'link.fields') &&
              <SolutionsButton href={props.link.fields.linkUrl}>
                {props.link.fields.linkText}
              </SolutionsButton>
            }
          </Grid.Item>

          {props.images && objectPath.has(props.images[1], 'fields.file.url') &&
            <Grid.Item styles={rightColumnStyles}>
              <RightImage className="js-slide-image">
                <LazyImg
                  width={335}
                  height={224}
                  src={`${props.images[1].fields.file.url}?w=335&h=224&fit=thumb&q=80&fm=jpg&fl=progressive`}
                  alt={props.images[1].fields.description}
                />
              </RightImage>
            </Grid.Item>
          }

        </Grid>

      </StoryContainer>

    </ImageSlideIn>
    <Waves
      position="bottom"
      alignment="bottom"
      height={2000}
      color="#fff"
    />
  </SolutionsSection>
);

const SolutionsSection = styled.section`
  position: relative;
  background-color: ${props => props.theme.lightGray};
  padding: 210px 0;
  text-align: center;
  overflow: hidden;

  h2 {
    white-space: nowrap;
  }

  p {
    margin: 70px auto 65px;

    ${screen.below(bp.tablet, `
      max-width: 470px;
      margin: 30px auto;
      grid-column: 6 / -1;
      grid-row: 2 / span 3;
    `)};

    ${screen.below(bp.mobile, `
      max-width: 218px;
    `)};
  }

  figure {
    position: relative;
  }

  img {
    max-width: 335px;
    max-height: 223px;

    @supports (filter: drop-shadow(24px 19px 49px rgba(0, 0, 0, .18))) {
      filter: drop-shadow(24px 19px 49px rgba(0, 0, 0, .18));
    }
  }

  ${screen.below(bp.tablet, `
    padding: 80px 0 140px;
  `)};
`;

const StoryContainer = styled(Container)`
  position: relative;
  z-index: 9;
`;

const LeftImage = styled.figure`
  transform: rotate(-20.4deg);
`;

const RightImage = styled.figure`
  transform: rotate(8.6deg);
`;

const SolutionsButton = styled(Button)`
  ${screen.below(bp.tablet, `
    width: 164px;
  `)}
`;

AboutSolutions.propTypes = {
  subheading: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
  images: PropTypes.array,
  link: PropTypes.object,
  id: PropTypes.string,
};

export default AboutSolutions;
